import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { useAuth } from "@/context/AuthContext";
import { inr, fmtDate, fmtDT } from "@/lib/format";
import { Field, PageHeader, Section, StatusBadge } from "@/components/common/kit";
import DataTable from "@/components/common/DataTable";

const EMPTY = { sku: "", brand: "", name: "", pack_size: 20, mrp: "", sales_price: "", purchase_cost: "", active: true, warehouse_id: "wh-cig", base_unit: "Pack", units: [], wholesale_price: "", reorder_level: "" };

function ProductForm({ product, onClose, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const isEdit = !!product?.id;
  useEffect(() => { if (product) setF({ ...EMPTY, ...product }); }, [product]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      const body = { sku: f.sku, brand: f.brand, name: f.name, pack_size: Number(f.pack_size), mrp: Number(f.mrp || 0), purchase_cost: Number(f.purchase_cost || 0), active: f.active,
        warehouse_id: f.warehouse_id, base_unit: f.base_unit, units: (f.units || []).filter((u) => u.name && u.name !== f.base_unit).map((u) => ({ name: u.name, factor: Number(u.factor) })),
        wholesale_price: Number(f.wholesale_price || 0), reorder_level: Number(f.reorder_level || 0) };
      if (isEdit) await api.put(`/products/${product.id}`, body);
      else await api.post("/products", { ...body, sales_price: Number(f.sales_price) });
      toast.success(isEdit ? "Product updated" : "Product created");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="product-form-dialog">
        <DialogHeader><DialogTitle>{isEdit ? `Edit ${product.name}` : "New Product"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="SKU / Product ID"><Input value={f.sku} onChange={set("sku")} data-testid="product-sku" /></Field>
          <Field label="Brand"><Input value={f.brand} onChange={set("brand")} data-testid="product-brand" /></Field>
          <div className="sm:col-span-2"><Field label="Product name / variant"><Input value={f.name} onChange={set("name")} data-testid="product-name" /></Field></div>
          <Field label="Pack size (sticks)"><Input type="number" value={f.pack_size} onChange={set("pack_size")} data-testid="product-pack-size" /></Field>
          <Field label="MRP (₹)"><Input type="number" value={f.mrp} onChange={set("mrp")} data-testid="product-mrp" /></Field>
          {!isEdit && <Field label="Sales price (₹/pack)"><Input type="number" value={f.sales_price} onChange={set("sales_price")} data-testid="product-sales-price" /></Field>}
          <Field label="Purchase cost (₹) · admin only"><Input type="number" value={f.purchase_cost} onChange={set("purchase_cost")} data-testid="product-purchase-cost" /></Field>
          <Field label="Warehouse"><select className="h-10 w-full rounded border px-2 text-sm" value={f.warehouse_id} onChange={(e) => setF({ ...f, warehouse_id: e.target.value, base_unit: e.target.value === "wh-cig" ? "Pack" : f.base_unit })} data-testid="product-warehouse"><option value="wh-cig">WH1 Cigarettes</option><option value="wh-other">WH2 Pan Masala & Other</option></select></Field>
          <Field label="Wholesale price (₹ per base unit)"><Input type="number" value={f.wholesale_price} onChange={set("wholesale_price")} data-testid="product-wholesale-price" /></Field>
          <Field label="Low stock alert at (base units)"><Input type="number" value={f.reorder_level} onChange={set("reorder_level")} /></Field>
          {f.warehouse_id === "wh-other" && <>
            <Field label="Base / stock unit (e.g. Packet)"><Input value={f.base_unit} onChange={set("base_unit")} data-testid="product-base-unit" /></Field>
            <div className="sm:col-span-2 space-y-1"><p className="text-xs font-semibold uppercase text-slate-600">Alternative units (1 unit = N base units)</p>
              {(f.units || []).filter((u) => u.name !== f.base_unit).concat([{ name: "", factor: "" }]).map((u, i) => <div key={i} className="flex gap-2">
                <Input placeholder="Unit e.g. Bora" value={u.name} onChange={(e) => { const l = (f.units || []).filter((x) => x.name !== f.base_unit); l[i] = { ...u, name: e.target.value }; setF({ ...f, units: l }); }} data-testid={`product-unit-name-${i}`} />
                <Input type="number" placeholder={`= ? ${f.base_unit}`} value={u.factor} onChange={(e) => { const l = (f.units || []).filter((x) => x.name !== f.base_unit); l[i] = { ...u, factor: e.target.value }; setF({ ...f, units: l }); }} data-testid={`product-unit-factor-${i}`} /></div>)}</div></>}
          <label className="flex items-center gap-2 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="product-active" /> Active</label>
        </div>
        {isEdit && <p className="text-xs text-slate-500">Use "Change price" to update the sales price with an effective date.</p>}
        <DialogFooter><Button onClick={save} data-testid="product-save-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PriceDialog({ product, onClose, onSaved }) {
  const { bizDate } = useAuth();
  const [price, setPrice] = useState("");
  const [eff, setEff] = useState(bizDate);
  const { data: hist, reload } = useFetch(product ? `/products/${product.id}/prices` : null);
  useEffect(() => { setPrice(""); setEff(bizDate); }, [product, bizDate]);
  const save = async () => {
    try {
      await api.post(`/products/${product.id}/price`, { price: Number(price), effective_date: eff });
      toast.success("Price updated");
      reload();
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="price-dialog">
        <DialogHeader><DialogTitle>Sales price · {product?.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Closed days keep their original prices. Open days on/after the effective date use the new price.</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="New price (₹/pack)"><Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="price-input" /></Field>
          <Field label="Effective date"><Input type="date" value={eff} onChange={(e) => setEff(e.target.value)} data-testid="price-effective-date" /></Field>
        </div>
        <Button onClick={save} disabled={!price} data-testid="price-save-button">Apply price</Button>
        <DataTable searchable={false} pageSize={0} rows={hist || []} testid="price-history-table" columns={[
          { key: "effective_date", label: "Effective", render: (r) => (r.effective_date === "2000-01-01" ? "Initial" : fmtDate(r.effective_date)) },
          { key: "previous_price", label: "Previous", type: "money" }, { key: "price", label: "New", type: "money" },
          { key: "changed_by", label: "By" }, { key: "created_at", label: "Changed at", type: "datetime" },
        ]} />
      </DialogContent>
    </Dialog>
  );
}

export default function Products() {
  const { data, loading, reload } = useFetch("/products");
  const [form, setForm] = useState(null);
  const [price, setPrice] = useState(null);
  const cols = [
    { key: "sku", label: "SKU" }, { key: "brand", label: "Brand" }, { key: "name", label: "Product" },
    { key: "pack_size", label: "Pack", type: "int" }, { key: "mrp", label: "MRP", type: "money" },
    { key: "sales_price", label: "Sales Price", type: "money", render: (r) => (
      <span className="font-mono">{inr(r.sales_price)}{r.scheduled_price && <span className="ml-1 text-xs text-amber-600">→ {inr(r.scheduled_price.price)} on {fmtDate(r.scheduled_price.effective_date)}</span>}</span>) },
    { key: "purchase_cost", label: "Purchase Cost", type: "money" },
    { key: "wholesale_price", label: "Wholesale", type: "money" },
    { key: "warehouse_id", label: "WH / Units", render: (r) => <span className="text-xs">{r.warehouse_id === "wh-other" ? "WH2" : "WH1"} · {(r.units || []).map((u) => u.factor === 1 ? u.name : `${u.name}=${u.factor}`).join(", ")}</span> },
    { key: "active", label: "Status", render: (r) => <StatusBadge status={r.active ? "ACTIVE" : "INACTIVE"} />, exportValue: (r) => (r.active ? "Active" : "Inactive") },
    { key: "updated_at", label: "Updated", render: (r) => <span className="text-xs">{fmtDT(r.updated_at)}</span> },
    { key: "actions", label: "", sortable: false, noExport: true, render: (r) => (
      <div className="flex justify-end gap-1">
        <Button size="sm" variant="ghost" onClick={() => setForm(r)} data-testid={`product-edit-${r.sku}`}>Edit</Button>
        <Button size="sm" variant="outline" onClick={() => setPrice(r)} data-testid={`product-price-${r.sku}`}>Change price</Button>
      </div>) },
  ];
  return (
    <div className="space-y-6">
      <PageHeader title="Products" subtitle="Product master · all quantities are in packs"
        actions={<Button onClick={() => setForm({})} data-testid="new-product-button"><Plus className="mr-1.5 h-4 w-4" /> New Product</Button>} />
      <Section><DataTable columns={cols} rows={data || []} loading={loading} title="Product Master" exportable testid="products-table" /></Section>
      <ProductForm product={form} onClose={() => setForm(null)} onSaved={() => { setForm(null); reload(); }} />
      <PriceDialog product={price} onClose={() => setPrice(null)} onSaved={reload} />
    </div>
  );
}
